import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listsApi, productsApi, searchApi, shoppingDateInput, type AggregateSummary, type ListItemDetail, type ListSuggestion, type ProductSummary, type ShoppingListSummary } from '../lib/domain';
import { useScope } from '../lib/hooks';
import { ApiError } from '../lib/api';
import { normalizeQuantity, parseQuantityQuery, quantityMin, quantityStep, useUnits } from '../lib/units';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faCheck, faPen, faRightLeft, faRotateLeft, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { groupByCategory } from '../lib/groupByCategory';
import { MoveToListControl } from '../components/MoveToListControl';

const STATUS_LABEL: Record<string, string> = { OPEN: 'offen', PARTIAL: 'teilweise', DONE: 'erledigt', UNAVAILABLE: 'nicht erhältlich' };
const STATUS_BADGE: Record<string, string> = { OPEN: '', PARTIAL: 'warn', DONE: 'ok', UNAVAILABLE: 'danger' };

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen. Bitte erneut versuchen.';
}

export function ListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [scope] = useScope();
  const units = useUnits(scope);
  const navigate = useNavigate();
  const [list, setList] = useState<Awaited<ReturnType<typeof listsApi.detail>>['data']['list'] | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductSummary[]>([]);
  const [aggregateResults, setAggregateResults] = useState<AggregateSummary[]>([]);
  const [ean, setEan] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('STUECK');
  const [error, setError] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [editUnit, setEditUnit] = useState('STUECK');
  const [otherLists, setOtherLists] = useState<ShoppingListSummary[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [suggestions, setSuggestions] = useState<ListSuggestion[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    const res = await listsApi.detail(id);
    setList(res.data.list);
    setFromCache(res.fromCache);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Issue #1: Positionen auf andere, nicht abgeschlossene Listen verschieben.
  useEffect(() => {
    if (!scope) return;
    listsApi
      .list(scope, 'OPEN')
      .then((r) => setOtherLists(r.data.lists.filter((l) => l.id !== id)))
      .catch(() => undefined);
  }, [scope, id, list]);

  // Automatische Vorschläge aus dem Kaufverhalten; ohne Verbindung einfach keine.
  const listStatus = list?.status;
  const itemCount = list?.items.length;
  useEffect(() => {
    if (!id || listStatus !== 'OPEN') {
      setSuggestions([]);
      return;
    }
    listsApi
      .suggestions(id)
      .then((r) => setSuggestions(r.suggestions))
      .catch(() => setSuggestions([]));
  }, [id, listStatus, itemCount]);

  const parsed = parseQuantityQuery(query, units.units, unit);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Vorschläge nach dem Erscheinen in den sichtbaren Bereich scrollen, damit
  // sie weder von der fixen Navigation noch vom Kopfbereich verdeckt werden.
  useEffect(() => {
    if (results.length > 0 || aggregateResults.length > 0) suggestionsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [results, aggregateResults]);

  async function search(q: string) {
    setQuery(q);
    // „5 Orangensaft“: Menge abtrennen und nur nach dem Produktnamen suchen.
    const name = parseQuantityQuery(q, units.units, unit).name;
    if (!scope || name.length < 2) {
      setResults([]);
      setAggregateResults([]);
      return;
    }
    const res = await searchApi.search(scope, name);
    setResults(res.products);
    setAggregateResults(res.aggregates ?? []);
  }

  // Sammelprodukt (z. B. „Äpfel“) auf die Liste setzen; beim Einkauf wird es
  // per EAN-Scan eines passenden Produkts abgehakt und der Preis dem
  // gescannten Produkt zugeordnet.
  async function addAggregate(aggregate: AggregateSummary) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.addItem(id, { aggregateId: aggregate.id, targetQuantity: parsed.quantity ?? quantity, unit: parsed.unit ?? unit });
      setQuery('');
      setResults([]);
      setAggregateResults([]);
      setQuantity(1);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  // Merkliste: allgemeinen Begriff (z. B. „Obst“) ohne konkrete Produktauswahl notieren.
  async function addFreeText() {
    if (!id || parsed.name.length < 1) return;
    setError(null);
    try {
      await listsApi.addItem(id, { freeText: parsed.name, targetQuantity: parsed.quantity ?? quantity, unit: parsed.unit ?? unit });
      setQuery('');
      setResults([]);
      setAggregateResults([]);
      setQuantity(1);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function addExisting(product: ProductSummary) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.addItem(id, { productId: product.id, targetQuantity: parsed.quantity ?? quantity, unit: parsed.unit ?? unit }, product);
      setQuery('');
      setResults([]);
      setAggregateResults([]);
      setQuantity(1); // Nutzeranfrage: Menge nach dem Hinzufügen wieder auf 1 zurücksetzen.
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function addSuggestion(s: ListSuggestion) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.addItem(id, { productId: s.product.id, targetQuantity: s.targetQuantity, unit: s.unit }, s.product);
      setSuggestions((prev) => prev.filter((x) => x.product.id !== s.product.id));
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  // forever=false: „Diesmal nicht“ (bis zum nächsten Kauf), true: „Nicht mehr vorschlagen“.
  async function dismissSuggestion(s: ListSuggestion, forever: boolean) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.dismissSuggestion(id, s.product.id, forever);
      setSuggestions((prev) => prev.filter((x) => x.product.id !== s.product.id));
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function addQuick() {
    if (!id || !scope || parsed.name.length < 2) return;
    setError(null);
    try {
      const created = await productsApi.quickCreate(scope, parsed.name, scope.startsWith('group:') ? scope.slice(6) : undefined);
      const productId = (created.result as { product: ProductSummary } | undefined)?.product.id;
      if (productId) {
        await listsApi.addItem(id, { productId, targetQuantity: parsed.quantity ?? quantity, unit: parsed.unit ?? unit });
      }
      setQuery('');
      setResults([]);
      setQuantity(1);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  // Per EAN hinzufügen: bereits bekanntes Produkt wird direkt verwendet,
  // eine unbekannte EAN wird per OpenFoodFacts aufgelöst und automatisch
  // angelegt (SHOP-005), erst danach schlägt es fehl. `eanValue` erlaubt den
  // direkten Aufruf mit einem gescannten Code, ohne auf den State-Update
  // aus dem Eingabefeld warten zu müssen.
  async function addByEan(eanValue?: string) {
    const value = (eanValue ?? ean).trim();
    if (!id || !scope || !value) return;
    setError(null);
    try {
      const resolved = await productsApi.resolveByEan(value, scope.startsWith('group:') ? scope.slice(6) : undefined);
      if (resolved.found === 'none' || !resolved.product) {
        setError(`Unbekannte EAN ${value}. Keine externen Daten gefunden - bitte Produkt manuell anlegen.`);
        return;
      }
      await listsApi.addItem(id, { productId: resolved.product.id, targetQuantity: quantity, unit });
      setEan('');
      setQuantity(1);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function resetItem(itemId: string) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.resetItem(id, itemId);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function startEdit(item: ListItemDetail) {
    setEditingItemId(item.id);
    setEditQuantity(Number(item.targetQuantity));
    setEditUnit(item.unit);
  }

  function stepEditQuantity(direction: 1 | -1) {
    const allowDecimal = units.allowDecimal(editUnit);
    setEditQuantity((q) => {
      const next = Math.round((q + direction) * 1000) / 1000;
      return Math.max(quantityMin(allowDecimal), normalizeQuantity(next, allowDecimal));
    });
  }

  async function saveEdit(itemId: string) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.updateItem(id, itemId, { targetQuantity: editQuantity, unit: editUnit });
      setEditingItemId(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function removeItem(itemId: string) {
    if (!id || !confirm('Position wirklich entfernen?')) return;
    setError(null);
    try {
      await listsApi.removeItem(id, itemId);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function moveItem(itemId: string, targetListId: string) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.moveItem(id, itemId, targetListId);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function toggleSelected(itemId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  // Sammelaktionen: nur noch vorhandene Positionen berücksichtigen.
  const selectedItems = (list?.items ?? []).filter((i) => selected.has(i.id));

  async function removeSelected() {
    if (!id || selectedItems.length === 0) return;
    if (!confirm(`${selectedItems.length} Position(en) wirklich entfernen?`)) return;
    setError(null);
    const failed = new Set<string>();
    for (const item of selectedItems) {
      try {
        await listsApi.removeItem(id, item.id);
      } catch {
        failed.add(item.id);
      }
    }
    setSelected(failed);
    if (failed.size > 0) setError(`${failed.size} Position(en) konnten nicht entfernt werden.`);
    await load();
  }

  // Verschieben ist nur für offene Positionen möglich (wie bei der Einzelaktion).
  async function moveSelected(targetListId: string) {
    if (!id) return;
    setError(null);
    const movable = selectedItems.filter((i) => i.status === 'OPEN');
    const failed = new Set<string>();
    for (const item of movable) {
      try {
        await listsApi.moveItem(id, item.id, targetListId);
      } catch {
        failed.add(item.id);
      }
    }
    const skipped = selectedItems.length - movable.length;
    setSelected(failed);
    if (failed.size > 0 || skipped > 0) {
      setError(`${failed.size + skipped} Position(en) wurden nicht verschoben (nur offene Positionen können verschoben werden).`);
    }
    await load();
  }

  async function complete(confirmOpenItems: boolean) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.complete(id, confirmOpenItems);
      navigate(`/lists/${id}/summary`);
    } catch (err) {
      if (!confirmOpenItems && err instanceof ApiError && err.code === 'CONFLICT' && confirm('Es gibt noch offene Positionen. Trotzdem abschließen?')) {
        await complete(true);
      } else {
        setError(errorMessage(err));
      }
    }
  }

  async function changeDate(value: string) {
    if (!id) return;
    setError(null);
    try {
      await listsApi.update(id, { shoppingDate: value || null });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function reopen() {
    if (!id) return;
    setError(null);
    try {
      await listsApi.reopen(id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  if (!list) return <p>Lade…</p>;
  const isOpen = list.status === 'OPEN';
  // Merkliste: wie eine Einkaufsliste, aber ohne Filiale, Datum und Einkaufsmodus.
  const isMemo = !!list.memoScopeKey;

  return (
    <div className="stack">
      <div className="row between">
        <h1>{isMemo ? '📝 Merkliste' : list.name || 'Einkaufsliste'}</h1>
        {isMemo ? <span className="badge">Nächster Einkauf</span> : <span className={`badge ${isOpen ? '' : 'ok'}`}>{isOpen ? 'Offen' : 'Abgeschlossen'}</span>}
      </div>
      {isMemo && <p className="muted">Hier landet alles, was beim nächsten Einkauf mitsoll. Auch allgemeine Begriffe ohne konkretes Produkt lassen sich als Notiz merken. Produkte lassen sich per Auswahl auf eine Einkaufsliste verschieben.</p>}
      {list.storeBranch && (
        <p className="muted">
          {list.storeBranch.chain.name} - {list.storeBranch.name}
        </p>
      )}
      {!isMemo && (
        <div className="field">
          <label htmlFor="shopping-date">Einkaufsdatum (optional)</label>
          <input
            id="shopping-date"
            type="date"
            value={shoppingDateInput(list.shoppingDate)}
            onChange={(e) => void changeDate(e.target.value)}
            disabled={!isOpen}
          />
        </div>
      )}
      {fromCache && <p className="muted">Offline-Ansicht</p>}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}

      {!isMemo && (
      <div className="row between">
        <Link to={`/lists/${id}/shop`}>
          <button className="primary">Einkaufsmodus starten</button>
        </Link>
        {isOpen ? (
          <button onClick={() => complete(false)}>Liste abschließen</button>
        ) : (
          <div className="row">
            <button onClick={reopen}>Liste wieder eröffnen</button>
            <button
              onClick={async () => {
                try {
                  const copy = await listsApi.copy(id!);
                  navigate(`/lists/${copy.list.id}`);
                } catch (err) {
                  setError(errorMessage(err));
                }
              }}
            >
              Als neue Liste kopieren
            </button>
          </div>
        )}
      </div>
      )}

      {isOpen && (
        <div className="card stack">
          <h2>Position hinzufügen</h2>
          <BarcodeScanner label="📷 EAN scannen" onDetect={(code) => addByEan(code)} onError={setError} />
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input
              type="number"
              min={quantityMin(units.allowDecimal(unit))}
              step={quantityStep(units.allowDecimal(unit))}
              style={{ width: 90, flex: '0 0 90px' }}
              value={quantity}
              onChange={(e) => setQuantity(normalizeQuantity(e.target.value, units.allowDecimal(unit)))}
              aria-label="Menge"
            />
            <select
              style={{ flex: 1, minWidth: 0 }}
              value={unit}
              onChange={(e) => {
                const nextUnit = e.target.value;
                setUnit(nextUnit);
                setQuantity((q) => normalizeQuantity(q, units.allowDecimal(nextUnit)));
              }}
              aria-label="Einheit"
            >
              {units.units.map((u) => (
                <option key={u.code} value={u.code}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
          <input
            id="product-search"
            aria-label="Produkt suchen oder neu anlegen"
            value={query}
            onChange={(e) => void search(e.target.value)}
            placeholder="Produkt suchen oder neu anlegen, z. B. Orangensaft"
          />
          {(results.length > 0 || aggregateResults.length > 0) && (
            <div ref={suggestionsRef} className="suggestions">
            <ul className="list-reset stack" style={{ gap: 2 }}>
              {aggregateResults.map((a) => (
                <li key={`agg-${a.id}`}>
                  <button className="suggestion" onClick={() => addAggregate(a)} style={{ width: '100%', textAlign: 'left' }}>
                    {a.name} <span className="badge">Sammelprodukt</span>
                  </button>
                </li>
              ))}
              {results.map((p) => (
                <li key={p.id}>
                  <button className="suggestion" onClick={() => addExisting(p)} style={{ width: '100%', textAlign: 'left' }}>
                    {p.name} {p.brand ? `(${p.brand})` : ''}
                  </button>
                </li>
              ))}
            </ul>
            </div>
          )}
          {isMemo && parsed.name.length >= 1 && (
            <button className="suggestion" onClick={addFreeText}>
              {parsed.quantity !== undefined ? `${parsed.quantity} × ` : ''}„{parsed.name}“ als Notiz ohne Produktauswahl merken
            </button>
          )}
          {parsed.name.length >= 2 && (
            <button className="suggestion" onClick={addQuick}>
              {parsed.quantity !== undefined ? `${parsed.quantity} × ` : ''}„{parsed.name}“ als neues Produkt anlegen und hinzufügen
            </button>
          )}
          {suggestions.length > 0 && (
            <div className="stack" style={{ gap: 4 }} aria-label="Vorschläge">
              <strong>Vorschläge</strong>
              <p className="muted">Regelmäßig gekauft und nach deinem üblichen Rhythmus wieder dran:</p>
              <ul className="list-reset stack" style={{ gap: 2 }}>
                {suggestions.map((s) => (
                  <li key={s.product.id} className="stack" style={{ gap: 2 }}>
                    <button className="suggestion" onClick={() => addSuggestion(s)} style={{ width: '100%', textAlign: 'left' }}>
                      + {s.targetQuantity} × {units.units.find((u) => u.code === s.unit)?.label ?? s.unit} {s.product.name}
                      {s.product.brand ? ` (${s.product.brand})` : ''}
                      <span className="muted">
                        {' '}
                        - gekauft etwa alle {s.intervalDays} Tage, zuletzt vor {s.daysSinceLast} Tagen
                      </span>
                    </button>
                    <div className="row">
                      <button onClick={() => dismissSuggestion(s, false)}>Diesmal nicht</button>
                      <button onClick={() => dismissSuggestion(s, true)}>Nicht mehr vorschlagen</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="muted">Ist das Produkt bereits auf der Liste, wird die Menge zusammengeführt statt dupliziert.</p>
          <div className="field">
            <label htmlFor="product-ean">Per EAN hinzufügen</label>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <input id="product-ean" value={ean} onChange={(e) => setEan(e.target.value)} placeholder="z. B. 4000539001618" />
              <button onClick={() => addByEan()} disabled={!ean.trim()}>
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && selected.size > 0 && (
        <div className="card row between" role="toolbar" aria-label="Sammelaktionen">
          <strong>{selected.size} ausgewählt</strong>
          <div className="row">
            <MoveToListControl
              lists={otherLists}
              onMove={moveSelected}
              triggerIcon={<FontAwesomeIcon icon={faRightLeft} />}
              triggerLabel="Auswahl verschieben"
            />
            <button onClick={() => setSelected(new Set())} aria-label="Auswahl aufheben" title="Auswahl aufheben">
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <button className="danger" onClick={removeSelected} aria-label="Auswahl entfernen" title="Auswahl entfernen">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      )}

      {groupByCategory(list.items).map((group) => (
      <div key={group.key} className="stack" style={{ gap: 'var(--space-2)' }}>
      <h3 style={{ margin: 'var(--space-2) 0 0' }}>{group.name}</h3>
      <ul className="list-reset stack">
        {group.items.map((item: ListItemDetail) => (
          <li key={item.id} className="card">
            <div className="row between">
              <label className="row" style={{ flexWrap: 'nowrap' }}>
                {isOpen && (
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelected(item.id)}
                    aria-label={`${item.product?.name ?? item.aggregate?.name ?? item.freeText ?? 'Position'} auswählen`}
                  />
                )}
                <strong>{item.product?.name ?? item.aggregate?.name ?? item.freeText ?? 'Unbekannt'}</strong>
                {item.freeText && <span className="badge">Notiz</span>}
              </label>
              <span className={`badge ${STATUS_BADGE[item.status]}`}>{STATUS_LABEL[item.status]}</span>
            </div>

            {editingItemId === item.id ? (
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  onClick={() => stepEditQuantity(-1)}
                  disabled={editQuantity <= quantityMin(units.allowDecimal(editUnit))}
                  aria-label="Menge verringern"
                  title="Menge verringern"
                >
                  −
                </button>
                <input
                  type="number"
                  min={quantityMin(units.allowDecimal(editUnit))}
                  step={quantityStep(units.allowDecimal(editUnit))}
                  style={{ width: 90 }}
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(normalizeQuantity(e.target.value, units.allowDecimal(editUnit)))}
                  aria-label="Menge"
                />
                <button onClick={() => stepEditQuantity(1)} aria-label="Menge erhöhen" title="Menge erhöhen">
                  +
                </button>
                <select
                  value={editUnit}
                  onChange={(e) => {
                    const nextUnit = e.target.value;
                    setEditUnit(nextUnit);
                    setEditQuantity((q) => normalizeQuantity(q, units.allowDecimal(nextUnit)));
                  }}
                  aria-label="Einheit"
                >
                  {units.units.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.label}
                    </option>
                  ))}
                </select>
                <button className="primary" onClick={() => saveEdit(item.id)} aria-label="Speichern" title="Speichern">
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button onClick={() => setEditingItemId(null)} aria-label="Abbrechen" title="Abbrechen">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ) : (
              <div className="muted">
                Soll {item.targetQuantity} {units.label(item.unit)} · gekauft {item.fulfilledQuantity} · nicht erhältlich {item.unavailableQuantity}
              </div>
            )}

            {isOpen && editingItemId !== item.id && (
              <div className="row" style={{ marginTop: 8 }}>
                <button onClick={() => startEdit(item)} aria-label="Menge bearbeiten" title="Menge bearbeiten">
                  <FontAwesomeIcon icon={faPen} />
                </button>
                {(item.productId || item.aggregateId) && (
                  <Link
                    to={item.productId ? `/products/${item.productId}` : `/aggregates/${item.aggregateId}`}
                    aria-label="Produkt bearbeiten"
                    title="Produkt bearbeiten"
                  >
                    <button type="button" aria-label="Produkt bearbeiten" title="Produkt bearbeiten">
                      <FontAwesomeIcon icon={faBoxOpen} />
                    </button>
                  </Link>
                )}
                {item.status !== 'OPEN' && (
                  <button onClick={() => resetItem(item.id)} aria-label="Auf offen zurücksetzen" title="Auf offen zurücksetzen">
                    <FontAwesomeIcon icon={faRotateLeft} />
                  </button>
                )}
                {item.status === 'OPEN' && (
                  <MoveToListControl
                    lists={otherLists}
                    onMove={(targetListId) => moveItem(item.id, targetListId)}
                    triggerIcon={<FontAwesomeIcon icon={faRightLeft} />}
                    triggerLabel="Verschieben"
                  />
                )}
                <button className="danger" onClick={() => removeItem(item.id)} aria-label="Entfernen" title="Entfernen">
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      </div>
      ))}
      {list.items.length === 0 && <p className="muted">Noch keine Positionen.</p>}

      {list.purchase && (
        <div className="card">
          <h2>Gesamtsumme</h2>
          <p>{Number(list.purchase.totalAmount).toFixed(2)} €</p>
        </div>
      )}
    </div>
  );
}
